import { useTranslation } from "react-i18next";

import { StyledMultiFileInput } from "@/components/shared/StyledMultiFileInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  anticipatedItemKind,
  type QuoteIntakeAnticipatedDocument,
} from "@/services/quoteIntakeService";
import type { QuoteRequestAttachmentUpload } from "@/services/quoteRequestsService";
import { filesInSlot } from "./quote-attachment-slots";

/**
 * What a file slot accepts.
 *
 * `.zip` is the batch escape hatch for a slot like "the last 12 months of
 * invoices" (spec b08814a3, D3). The server decides on the declared MIMETYPE,
 * not on this attribute — `accept` is a file-picker filter and nothing more.
 */
export const ANTICIPATED_ACCEPT = ".pdf,image/*,.zip";

/**
 * Which heading this section gets. "Documentos que ayudan a cotizar" stops
 * being true the moment one of the items is a number to type, and a heading
 * that says "documentos" over a text box is the same mismatch this feature
 * exists to remove — one level up.
 */
export function anticipatedSectionCopy(items: QuoteIntakeAnticipatedDocument[]): {
  labelKey: string;
  hintKey: string;
} {
  const hasText = items.some((item) => anticipatedItemKind(item) === "text");
  return hasText
    ? {
        labelKey: "requests.user.quoteRequestAnticipatedMixedLabel",
        hintKey: "requests.user.quoteRequestAnticipatedMixedHint",
      }
    : {
        labelKey: "requests.user.quoteRequestAnticipatedLabel",
        hintKey: "requests.user.quoteRequestAnticipatedHint",
      };
}

export interface AnticipatedItemFieldsProps {
  /** Namespaces the input ids, since the review and the form can both be mounted. */
  idPrefix: string;
  items: QuoteIntakeAnticipatedDocument[];
  attachments: QuoteRequestAttachmentUpload[];
  /** Replace one item's slot with this list of files. */
  onSlotFilesChange: (item: QuoteIntakeAnticipatedDocument, files: File[]) => void;
  /** Typed answers so far, keyed by the item's label. */
  answers: Record<string, string>;
  onAnswerChange: (label: string, value: string) => void;
  /** How many more files the whole request can still take, across all slots. */
  remainingAttachments: number;
}

/**
 * The things the conversation suggested providing, each rendered as the
 * control that actually fits it (spec 13810fd2).
 *
 * Every item used to be a file dropzone, because the schema had no way for the
 * intake to say otherwise. That answered "RUT o número de BPS de la SAS" —
 * twelve digits the person knows by heart — with **Seleccionar archivo**, and
 * an item answered with the wrong control is an item nobody answers: the
 * consumer goes looking for a certificate they were never asked for, or skips
 * it, and the professional prices the job without the identifier that would
 * have told them what exists in the registry.
 *
 * A file item now takes MANY files (spec b08814a3). One was never enough for
 * the question actually being asked: "facturas o comprobantes del período
 * (últimos 12 meses)" is twelve documents, and a single slot answered it by
 * silently replacing whatever was already there.
 *
 * One component for both entry points so the two cannot drift: the same item
 * must not be a text box on the review screen and a dropzone on the form.
 */
export function AnticipatedItemFields({
  idPrefix,
  items,
  attachments,
  onSlotFilesChange,
  answers,
  onAnswerChange,
  remainingAttachments,
}: AnticipatedItemFieldsProps): JSX.Element {
  const { t } = useTranslation();

  return (
    /**
     * INDENTED behind a rule, and spaced further apart than the parts of any
     * one item.
     *
     * Both are doing the same job: making each request read as one object
     * nested under the section heading. Flush-left at space-y-4, the gap
     * BETWEEN two items was 16px while the gap between a label and its own
     * hint was 6px — close enough that the eye grouped a hint with the field
     * below it rather than the label above it, and the whole block read as one
     * long ladder of same-sized text. The rule supplies the nesting the flat
     * layout could not.
     */
    <div className="space-y-6 border-l-2 border-border/60 pl-4">
      {items.map((item) => {
        const inputId = `${idPrefix}-${item.label}`;
        const isText = anticipatedItemKind(item) === "text";
        const slotFiles = isText ? [] : filesInSlot(attachments, item.label);
        return (
          // space-y-1 inside, space-y-6 outside: the label, its hint and its
          // control belong together, and proximity is what says so.
          <div key={item.label} className="space-y-1">
            {/* font-medium, where the section heading above is semibold at a
                LARGER size. Previously both were text-sm and differed only by
                weight, so the heading barely outranked the things under it. */}
            <Label htmlFor={inputId} className="text-sm font-medium text-foreground">
              {item.label}
            </Label>
            <p className="pb-1 text-xs leading-relaxed text-muted-foreground">{item.why}</p>
            {isText ? (
              <Input
                id={inputId}
                value={answers[item.label] ?? ""}
                onChange={(e) => onAnswerChange(item.label, e.target.value)}
                placeholder={t("requests.user.quoteRequestAnticipatedValuePlaceholder")}
                className="bg-card dark:bg-background"
              />
            ) : (
              <StyledMultiFileInput
                id={inputId}
                accept={ANTICIPATED_ACCEPT}
                value={slotFiles.map((a) => a.file)}
                onChange={(files) => onSlotFilesChange(item, files)}
                /**
                 * maxFiles is per-slot, while the real ceiling is per-REQUEST
                 * across every slot — so it is this slot's own files plus
                 * whatever room is left overall. That makes the control's
                 * at-capacity state fire at the same moment the request
                 * actually fills up, instead of per slot.
                 *
                 * At capacity this component disables only the ADD button and
                 * leaves every remove button live, which is the behaviour that
                 * matters: a consumer who hits the ceiling must still be able
                 * to swap a file out.
                 */
                maxFiles={slotFiles.length + Math.max(0, remainingAttachments)}
                // On the control itself rather than under the section heading:
                // someone with forty invoices needs the suggestion at the
                // moment they are about to pick them one at a time, not in a
                // paragraph they read before they had any.
                hint={t("requests.user.quoteRequestZipHint")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
